import { useMemo, cloneElement } from "react";
import { useTheme } from "../hooks/use-theme";
import { ColorValue, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import Gap from "../ui_atoms/Gap";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";

export type BubbleTalkPosition =
    | "top-left"
    | "top-center"
    | "top-right"
    | "center-left"
    | "center-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";

type BubbleTalkProps = {
    personImageComponent: React.ReactElement;
    personSize?: number;
    position: BubbleTalkPosition;
    bubbleBackgroundColor?: ColorValue;
    personStyle?: StyleProp<ViewStyle>;
    children: React.ReactElement;
};

const DEFAULT_PERSON_SIZE = 84;

export const BubbleTalk = ({
    personImageComponent,
    personSize = DEFAULT_PERSON_SIZE,
    children,
    position,
    bubbleBackgroundColor,
    personStyle,
}: BubbleTalkProps) => {
    const styles = useStyles();
    const isLarge = useIsScreenSizeMin("large");

    const { containerStyle, messageStyle, imageStyle, arrowStyle, componentStyle } = useMemo(
        () => getPositionStyles(position, personSize, isLarge),
        [isLarge, personSize, position],
    );

    return (
        <View style={containerStyle}>
            <View
                style={[
                    styles.bubble,
                    messageStyle,
                    bubbleBackgroundColor && { backgroundColor: bubbleBackgroundColor },
                ]}
            >
                <View
                    style={[
                        styles.arrow,
                        arrowStyle,
                        bubbleBackgroundColor && { backgroundColor: bubbleBackgroundColor },
                    ]}
                />
                {children ? cloneElement(children, { style: componentStyle }) : null}
            </View>
            <Gap gap={1} />
            <View
                style={[
                    styles.imageContainer,
                    imageStyle,
                    personStyle,
                    {
                        width: personSize,
                        height: personSize,
                    },
                    bubbleBackgroundColor && { backgroundColor: bubbleBackgroundColor },
                ]}
            >
                {cloneElement(personImageComponent, { width: "100%", height: "100%" })}
            </View>
        </View>
    );
};

const getPositionStyles = (position: BubbleTalkPosition, personSize: number, isLarge: boolean) => {
    const adjustedArrowMargin = ARROW_SIZE * 0.9;
    switch (position) {
        case "top-left":
            return {
                containerStyle: { flexDirection: "column-reverse", alignItems: "flex-start" },
                messageStyle: {
                    width: !isLarge ? "100%" : undefined,
                },
                imageStyle: {
                    left: BUBBLE_MARGIN,
                },
                arrowStyle: {
                    top: ARROW_OFFSET,
                    left: BUBBLE_MARGIN + adjustedArrowMargin,
                },
            } as const;
        case "top-center":
            return {
                containerStyle: { flexDirection: "column-reverse", alignItems: "center" },
                messageStyle: {
                    width: !isLarge ? "100%" : undefined,
                    alignItems: "center",
                    textAlign: "start",
                },
                arrowStyle: {
                    top: ARROW_OFFSET,
                },
                componentStyle: {
                    textAlign: "center",
                },
            } as const;
        case "top-right":
            return {
                containerStyle: { flexDirection: "column-reverse", alignItems: "flex-end" },
                messageStyle: {
                    width: !isLarge ? "100%" : undefined,
                },
                imageStyle: {
                    right: BUBBLE_MARGIN,
                },
                arrowStyle: {
                    top: ARROW_OFFSET,
                    right: BUBBLE_MARGIN + adjustedArrowMargin,
                },
            } as const;
        case "center-left":
            return {
                containerStyle: {
                    flexDirection: "row",
                    justifyContent: "flex-start",
                },
                messageStyle: { marginRight: 8, flex: 1 },
                arrowStyle: {
                    right: ARROW_OFFSET,
                    top: personSize / 2 - ARROW_SIZE / 2,
                },
            } as const;
        case "center-right":
            return {
                containerStyle: {
                    flexDirection: "row-reverse",
                    justifyContent: "flex-end",
                },
                messageStyle: { marginLeft: 8, flex: 1 },
                arrowStyle: {
                    left: ARROW_OFFSET,
                    top: personSize / 2 - ARROW_SIZE / 2,
                },
            } as const;
        case "bottom-left":
            return {
                containerStyle: { flexDirection: "column", alignItems: "flex-start" },
                messageStyle: {
                    width: !isLarge ? "100%" : undefined,
                },
                imageStyle: {
                    left: BUBBLE_MARGIN,
                },
                arrowStyle: {
                    bottom: ARROW_OFFSET,
                    left: BUBBLE_MARGIN + adjustedArrowMargin,
                },
            } as const;
        case "bottom-center":
            return {
                containerStyle: { flexDirection: "column", alignItems: "center" },
                messageStyle: {
                    width: !isLarge ? "100%" : undefined,
                    alignItems: "center",
                },
                arrowStyle: {
                    bottom: ARROW_OFFSET,
                },
                componentStyle: {
                    textAlign: "center",
                },
            } as const;
        case "bottom-right":
            return {
                containerStyle: { flexDirection: "column", alignItems: "flex-end" },
                messageStyle: {
                    width: !isLarge ? "100%" : undefined,
                },
                imageStyle: {
                    right: BUBBLE_MARGIN,
                },
                arrowStyle: {
                    bottom: ARROW_OFFSET,
                    right: BUBBLE_MARGIN + adjustedArrowMargin,
                },
            } as const;
        default:
            return {};
    }
};

const ARROW_SIZE = 30;
const BUBBLE_MARGIN = 20;
const ARROW_OFFSET = -6;

const useStyles = () => {
    const { colors, gridSize, roundness } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                imageContainer: {
                    borderRadius: 100,
                    backgroundColor: colors.accentLighter,
                    overflow: "hidden",
                    alignItems: "center",
                    justifyContent: "center",
                },
                arrow: {
                    position: "absolute",
                    width: ARROW_SIZE,
                    height: ARROW_SIZE,
                    backgroundColor: colors.accentLighter,
                    transform: [{ rotate: "45deg" }],
                    borderRadius: roundness / 3,
                },
                bubble: {
                    paddingHorizontal: gridSize / 2,
                    paddingVertical: gridSize / 3,
                    borderRadius: roundness * 2,
                    backgroundColor: colors.accentLighter,
                },
            }),
        [colors.accentLighter, gridSize, roundness],
    );
};
